// Simple image loader without HEIC conversion dependencies
export interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  filename: string;
  tall?: boolean;
}

// Type for webpack's require.context
type RequireContext = {
  keys(): string[];
  (id: string): any;
  <T>(id: string): T;
  resolve(id: string): string;
  id: string;
};

// Load all images from assets folder (works with any filenames)
export const loadAllGalleryImages = (): GalleryImage[] => {
  const importAll = (r: RequireContext) => {
    const images: { [key: string]: string } = {};
    r.keys().forEach((item: string) => {
      images[item.replace('./', '')] = r(item);
    });
    return images;
  };

  // Load all supported image formats
  const images = importAll(
    (require as any).context('../assets', false, /\.(png|jpe?g|svg|gif|bmp|webp)$/i)
  );

  // Convert to gallery format with random layout
  const galleryImages: GalleryImage[] = Object.entries(images).map(([filename, src], index) => {
    // Create readable alt text from filename
    const cleanName = filename
      .replace(/\.[^/.]+$/, '') // Remove extension
      .replace(/[-_]/g, ' ')    // Replace dashes/underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letters

    // Deterministic tall/short pattern for masonry variety (stable across renders)
    const tall = index % 3 === 1;

    return {
      id: index + 1,
      src: src,
      alt: `${cleanName}`,
      filename: filename,
      tall: tall
    };
  });

  return galleryImages;
};

// Load only specific image types
export const loadImagesByExtension = (extensions: string[] = ['jpg', 'jpeg', 'png', 'webp']): GalleryImage[] => {
  const extensionPattern = new RegExp(`\\.(${extensions.join('|')})$`, 'i');
  
  const importAll = (r: RequireContext) => {
    const images: { [key: string]: string } = {};
    r.keys().forEach((item: string) => {
      if (extensionPattern.test(item)) {
        images[item.replace('./', '')] = r(item);
      }
    });
    return images;
  };

  const images = importAll(
    (require as any).context('../assets', false, /\.(png|jpe?g|svg|gif|bmp|webp)$/i)
  );

  return Object.entries(images).map(([filename, src], index) => {
    const cleanName = filename
      .replace(/\.[^/.]+$/, '')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    const tall = Math.random() > 0.6;

    return {
      id: index + 1,
      src: src,
      alt: `${cleanName}`,
      filename: filename,
      tall: tall
    };
  });
};
